import React, { useState, useEffect } from 'react';
import { getQueueStats, syncIfOnline } from '../utils/offlineQueue';
import { showError } from '../utils/toast';
import { twMerge } from 'tailwind-merge';
import { Button } from './ui';

interface QueueStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export const OfflineIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<{ succeeded: number; failed: number } | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); loadQueueStats(); };
    const handleOffline = () => { setIsOnline(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    loadQueueStats();
    const interval = setInterval(() => { loadQueueStats(); }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SYNC_COMPLETE') {
        setLastSyncResult({ succeeded: event.data.succeeded, failed: event.data.failed });
        setIsSyncing(false);
        loadQueueStats();
        setTimeout(() => setLastSyncResult(null), 5000);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => { navigator.serviceWorker?.removeEventListener('message', handleMessage); };
  }, []);

  const loadQueueStats = async () => {
    try {
      const stats = await getQueueStats();
      setQueueStats(stats);
    } catch (error) {
      // intentional: background polling every 10s, stale stats remain visible
    }
  };

  const handleSyncNow = async () => {
    if (!isOnline) {
      showError('Cannot sync while offline. Please check your internet connection.');
      return;
    }
    setIsSyncing(true);
    try {
      await syncIfOnline();
    } catch (error) {
      showError('Sync failed. Please try again.');
      setIsSyncing(false);
    }
  };

  if (isOnline && (!queueStats || queueStats.pending === 0) && !lastSyncResult) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none [&>*]:pointer-events-auto">
      {!isOnline && (
        <div className="flex items-center gap-3 py-2.5 px-4 md:py-3 md:px-6 bg-warning text-ink text-sm font-medium shadow-md">
          <div className="flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
              <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
            </svg>
          </div>
          <span>You're offline. Changes will sync when you reconnect.</span>
        </div>
      )}

      {queueStats && queueStats.pending > 0 && (
        <div className="bg-bg-secondary border border-border-light border-t-0 shadow-md">
          <button
            className="flex items-center gap-2 py-2.5 px-4 md:py-3 md:px-6 w-full bg-transparent border-none text-sm font-medium text-text-primary cursor-pointer transition-colors hover:bg-bg-tertiary"
            onClick={() => setShowDetails(!showDetails)}
          >
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-aegean text-text-on-accent rounded-lg text-xs font-semibold">
              {queueStats.pending}
            </span>
            <span>Pending changes</span>
            <svg
              className={twMerge('ml-auto transition-transform duration-200', showDetails && 'rotate-180')}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {showDetails && (
            <div className="px-4 md:px-6 pb-2.5 md:pb-3 border-t border-border-light">
              <div className="flex gap-3 md:gap-6 py-3 flex-wrap">
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="text-text-secondary">Pending:</span>
                  <span className="font-semibold text-text-primary">{queueStats.pending}</span>
                </div>
                {queueStats.processing > 0 && (
                  <div className="flex items-center gap-2 text-[13px]">
                    <span className="text-text-secondary">Syncing:</span>
                    <span className="font-semibold text-text-primary">{queueStats.processing}</span>
                  </div>
                )}
                {queueStats.failed > 0 && (
                  <div className="flex items-center gap-2 text-[13px]">
                    <span className="text-text-secondary">Failed:</span>
                    <span className="font-semibold text-danger">{queueStats.failed}</span>
                  </div>
                )}
              </div>

              {isOnline && !isSyncing && (
                <Button
                  variant="primary"
                  size="sm"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium"
                  onClick={handleSyncNow}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                  Sync Now
                </Button>
              )}

              {isSyncing && (
                <div className="flex items-center gap-2 py-2 text-text-secondary text-[13px]">
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  Syncing...
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {lastSyncResult && (
        <div className={twMerge(
          'flex items-center gap-2 py-2.5 px-4 md:py-3 md:px-6 text-text-inverse text-sm font-medium shadow-md',
          lastSyncResult.failed > 0 ? 'bg-warning' : 'bg-success',
        )}>
          {lastSyncResult.failed === 0 ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
              <span>All changes synced ({lastSyncResult.succeeded})</span>
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                Synced {lastSyncResult.succeeded}, {lastSyncResult.failed} failed
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
